import type { Invalidation } from "../../tx/invalidation.ts";
import type { Option } from "../../core/option.ts";
import { Tracing } from "@tento-core/tracing";

import type { TxFrameJoin } from "./tx-frame-join.ts";

/**
 * `frame` is the post-subsume effect map the flush drains from — the whole
 * frame's dirty set, so an executor can resolve cross-effect coverage (e.g.
 * skip a narrow scope when a whole-sheet effect is also present this frame)
 * without cross-frame state. Existing 2-arg executors stay assignable (TS
 * allows a callback with fewer parameters).
 */
export type EffectExecutor = (
  effectId: string,
  sources: Invalidation[],
  frame: ReadonlyMap<string, Invalidation[]>,
) => void;

/**
 * Schedules a transaction flush for "end of frame". Production passes
 * the fastdom mutate phase (`(flush) => $$.mutate(flush)`); headless
 * tests capture the callback and flush synchronously — the seam that
 * lets the V1→V3 pipeline run under Deno with zero DOM (D7).
 */
export type FlushScheduler = (flush: () => void) => void;

/**
 * A frame's effect ids across subsume — the two ordered arrays the
 * `invalidation/frame` log carries.
 */
export interface SubsumeDiff {
  /** Effect ids that survived subsume, in drain (`order`) order. */
  readonly effects: string[];
  /** Effect ids `subsume` removed from the frame. */
  readonly subsumed: string[];
}

/**
 * Pure per-frame subsume diff. Split out so the
 * headless pipeline test can assert the exact computation
 * `Factory.flush` delegates to — that keeps the flush-time
 * `Tracing.log("invalidation/frame", …)` emission trivially correct.
 *
 * @param beforeIds effect ids present BEFORE subsume (map insertion order)
 * @param after     the effects map AFTER subsume mutated it in place
 * @param order     the drain order (`EFFECT_ORDER`)
 */
export function subsumeDiff(
  beforeIds: readonly string[],
  after: ReadonlyMap<string, Invalidation[]>,
  order: readonly string[],
): SubsumeDiff {
  return {
    effects: order.filter((id) => after.has(id)),
    subsumed: beforeIds.filter((id) => !after.has(id)),
  };
}

/**
 * Per-frame accumulator for invalidation effects. Collects effects via
 * addEffect(), deduplicates by effect id + event .eq(), and drains on
 * flush.
 */
export class InvalidationTransaction {
  #effects = new Map<string, Invalidation[]>();
  /**
   * Dedupe index: effect id → source path → the sources already held
   * under that effect with that path. `.eq()` is only ever consulted
   * within one path bucket, which keeps `addEffect` O(1) amortized.
   *
   * Why: a structural storm (timescale switch, sheet swap) emits tens of
   * thousands of distinct `ColumnDataChanged`/`RowDataChanged` events
   * into ONE frame, most resolving to the same effect id. Deduping with a
   * linear `sources.some(eq)` made the frame O(n²) — ~0.4s of `eq` per
   * switch in the sdk2 traces, the largest pure-JS task of the battery.
   *
   * Soundness rests on one invariant of the `@tento/lona` vocabulary:
   * `a.eq(b)` implies `a.path === b.path` (every subclass derives its
   * path from the same fields its `eq` compares, or from a subset). So a
   * path bucket sees every candidate the linear scan would have matched
   * and the dedupe result is byte-identical. The invalidation-pipeline
   * suite pins that invariant across the whole event vocabulary.
   */
  #byPath = new Map<string, Map<string, Invalidation[]>>();

  /** Add an effect with its source event. Dedupes by effect id + event .eq(). */
  addEffect(effectId: string, source: Invalidation): void {
    let sources = this.#effects.get(effectId);
    let paths = this.#byPath.get(effectId);
    if (!sources || !paths) {
      sources = [];
      paths = new Map();
      this.#effects.set(effectId, sources);
      this.#byPath.set(effectId, paths);
    }
    let bucket = paths.get(source.path);
    if (!bucket) {
      bucket = [];
      paths.set(source.path, bucket);
    }
    if (bucket.some((s) => s.eq(source))) return;
    bucket.push(source);
    sources.push(source);
  }

  /** Drain all accumulated effects. Clears the transaction. */
  drain(): Map<string, Invalidation[]> {
    const result = this.#effects;
    this.#effects = new Map();
    this.#byPath = new Map();
    return result;
  }
}

/**
 * Factory: creates per-frame transactions with fixed config.
 * .current() returns the active transaction or creates a new one,
 * scheduling a flush for the end of the frame via the injected
 * `FlushScheduler`.
 */
export namespace InvalidationTransaction {
  export class Factory {
    #instance: Option<InvalidationTransaction> = null;

    #txJoin: Option<TxFrameJoin>;
    #schedule: FlushScheduler;
    #execute: EffectExecutor;
    #order: string[];
    #subsume: (effects: Map<string, Invalidation[]>) => void;
    constructor(
      subsume: (effects: Map<string, Invalidation[]>) => void,
      order: string[],
      execute: EffectExecutor,
      schedule: FlushScheduler,
      // TX1 — the pre-subsume frame join (WAL ⋈ V3 frame). `null` ⇒ no join
      // (the pipeline test's minimal Factory); when present, `flush` stamps the
      // joined attribution fields onto the one `invalidation/frame` log.
      txJoin: Option<TxFrameJoin>,
    ) {
      this.#subsume = subsume;
      this.#order = order;
      this.#execute = execute;
      this.#schedule = schedule;
      this.#txJoin = txJoin;
    }

    /** Get or create the current per-frame transaction. */
    current(): InvalidationTransaction {
      if (!this.#instance) {
        this.#instance = new InvalidationTransaction();
        this.#schedule(() => this.#flush());
      }
      return this.#instance;
    }

    /** Re-entrancy witness — if an effect executor schedules another
     *  flush mid-flight (i.e. invalidates while inside a flush),
     *  emit `witness/reentry`. "log" mode keeps existing behavior
     *  (we don't want to break the reactive cycle, just see it). */
    static readonly #flushReentrancy = new Tracing.Witness.Reentrancy({
      name: "invalidation-transaction/flush",
      onReentry: "log",
    });

    #flush(): void {
      const tx = this.#instance;
      this.#instance = null;
      if (!tx) return;

      const effects = tx.drain();
      if (effects.size === 0) return;

      // Per-frame observability for `invalidation/frame`. Snapshot the
      // pre-subsume effect ids + total source count once (the
      // effects.size === 0 return above already skips empty frames), so
      // the post-subsume diff below names exactly what this frame
      // coalesced away.
      const beforeIds = [...effects.keys()];
      let sourceCount = 0;
      for (const sources of effects.values()) sourceCount += sources.length;

      const exitReentry = Factory.#flushReentrancy.enter({
        effectCount: effects.size,
      });
      try {
        this.#subsume(effects);

        // One structured `invalidation/frame` log per flush. subsumeDiff
        // builds the surviving/subsumed arrays once; the emission stays
        // trivially correct because that pure fn is what the headless
        // pipeline test asserts.
        const diff = subsumeDiff(beforeIds, effects, this.#order);
        // TX1 — join the WAL to this frame (pre-subsume capture + the τ
        // bracket). `beforeIds` is the genuine pre-subsume identity set snapshot
        // above (before `subsume` mutated the map); `diff.effects` is the
        // post-subsume survivor id set (drain order). `observe` stays the single
        // computation the per-transaction locality fixtures assert, so this
        // emission is trivially correct. Under the union-only law beforeIds and
        // survivors coincide; a dropped id would flag a lossy coalescer.
        // Absent join (pipeline test) ⇒ the fields are simply omitted.
        const join = this.#txJoin?.observe(beforeIds, diff.effects) ?? null;
        Tracing.log("invalidation/frame", {
          effects: diff.effects,
          subsumed: diff.subsumed,
          sources: sourceCount,
          // TX1 additive fields (additive-only — existing frame consumers ignore).
          beforeIds: join?.beforeIds ?? null,
          beforeCount: join?.beforeCount ?? null,
          afterCount: join?.afterCount ?? null,
          tauAtFlush: join?.tauAtFlush ?? null,
          txnsAttributed: join?.txnsAttributed ?? null,
          txnIds: join?.txnIds ?? null,
        });

        for (const id of this.#order) {
          const sources = effects.get(id);
          if (sources) this.#execute(id, sources, effects);
        }
      } finally {
        exitReentry();
      }
    }
  }
}
